import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { InternalAudit, Paginated } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Button,
  EmptyState,
  LoadingPane,
  PageHeader,
  ProgressBar,
  Table,
  formatDate,
} from '../../components/ui.js';
import { AuditFormDialog } from './AuditFormDialog.js';
import { AuditStatusBadge, FindingCountBadges, auditsListKey, progressPercent } from './shared.js';
import './audits.css';

const PAGE_SIZE = 25;

/** Internal audits list (/internal-audits). */
export function InternalAuditsPage() {
  const { org } = useOrg();
  const canPlan = org.role === 'admin' || org.role === 'reviewer';
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(false);

  const audits = useQuery({
    queryKey: [...auditsListKey(org.orgId), offset],
    queryFn: () =>
      apiFetch<Paginated<InternalAudit>>(`/orgs/${org.orgId}/internal-audits`, {
        query: { limit: PAGE_SIZE, offset },
      }),
    enabled: org.role !== 'assessor',
  });

  if (org.role === 'assessor') {
    return (
      <EmptyState
        title="Members only"
        message="Internal audits are visible to members of the organisation, not to external assessors."
      />
    );
  }
  if (audits.isLoading && offset === 0) return <LoadingPane label="Loading internal audits" />;

  const items = audits.data?.items ?? [];
  const total = audits.data?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="Internal Audits"
        subtitle="Plan an audit, let the AI auditor desk-review the evidence for the clauses in scope, then record findings and the report"
        actions={canPlan && <Button onClick={() => setOpen(true)}>New audit</Button>}
      />

      {total === 0 ? (
        <EmptyState
          title="No internal audits yet"
          message={
            canPlan
              ? 'Plan your first audit: choose a standard version, the clauses in scope and, optionally, the assessment whose responses and evidence the AI auditor examines.'
              : 'No internal audits have been planned for this organisation yet.'
          }
          action={canPlan ? <Button onClick={() => setOpen(true)}>Plan your first audit</Button> : undefined}
        />
      ) : (
        <>
          <Table
            head={
              <>
                <th>Name</th>
                <th>Standard</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Findings</th>
                <th>Lead auditor</th>
                <th>Planned</th>
                <th>
                  <span className="sr-only">Open</span>
                </th>
              </>
            }
          >
            {items.map((a) => (
              <tr key={a.id}>
                <td style={{ fontWeight: 600 }}>
                  <Link to={`/internal-audits/${a.id}`}>{a.name}</Link>
                </td>
                <td>
                  {a.standardName} <span className="audit-muted">{a.versionLabel}</span>
                </td>
                <td>
                  <span title={a.scope.map((s) => `${s.number} ${s.title}`).join('\n')}>
                    {a.scope.map((s) => s.number).join(', ') || '—'}
                  </span>
                </td>
                <td>
                  <AuditStatusBadge status={a.status} />
                </td>
                <td className="audit-progress-cell">
                  <span className="audit-nowrap">
                    {a.progress.done + a.progress.skipped}/{a.progress.total}
                    {a.progress.failed > 0 && (
                      <span className="audit-error-inline"> · {a.progress.failed} failed</span>
                    )}
                  </span>
                  <ProgressBar percent={progressPercent(a.progress)} label={`Progress of ${a.name}`} />
                </td>
                <td>
                  <FindingCountBadges counts={a.counts} />
                </td>
                <td>{a.leadAuditorName ?? '—'}</td>
                <td className="audit-nowrap">{formatDate(a.plannedOn)}</td>
                <td>
                  <Link to={`/internal-audits/${a.id}`}>
                    <Button size="sm" variant="secondary">
                      Open
                    </Button>
                  </Link>
                </td>
              </tr>
            ))}
          </Table>

          {total > PAGE_SIZE && (
            <div className="audit-pager">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <span className="audit-muted">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <AuditFormDialog open={open} onClose={() => setOpen(false)} audit={null} />
    </div>
  );
}
